import React, { useState } from 'react';
import { Form, Input, Button, Alert, Checkbox } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useAuthStore } from '../../stores/authStore';
import { TermsModal } from './TermsModal';
import { TestVersionNotice } from '../common/TestVersionNotice';

import './AuthStyles.css';

interface LoginFormProps {
  onSuccess?: () => void;
  onSwitchToRegister?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, onSwitchToRegister }) => {
  const [form] = Form.useForm();
  const { login, isLoading, error, clearError } = useAuthStore();
  const [termsModalVisible, setTermsModalVisible] = useState(false);
  const [privacyModalVisible, setPrivacyModalVisible] = useState(false);


  const handleSubmit = async (values: { username: string; password: string }) => {
    clearError();
    
    try {
      await login(values.username, values.password);
      onSuccess?.();
    } catch (error) {
      // Error is handled by the store
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <TestVersionNotice variant="auth" />
        {/* Logo Section */}
        <div className="logo-section">
          <div className="hexagon-logo">
            <img src="/images/GH-logo.png" alt="GH Logo" />
          </div>
          <h1 className="brand-text">WaveLink</h1>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <Alert
              message="登录失败"
              description={error}
              type="error"
              showIcon
              closable
            />
          </div>
        )}

        {/* Login Form */}
        <Form
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
          className="auth-form"
        >
          <Form.Item
            name="username"
            rules={[{ required: true, message: '请输入用户名' }]}
            className="form-item"
          >
            <Input
              prefix={<UserOutlined className="input-icon" />}
              placeholder="用户名"
              className="auth-input"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: '请输入密码' }]}
            className="form-item"
          >
            <Input.Password
              prefix={<LockOutlined className="input-icon" />}
              placeholder="密码"
              className="auth-input"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="agreedToTerms"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value ? Promise.resolve() : Promise.reject(new Error('请先阅读并同意用户协议和隐私政策')),
              },
            ]}
            className="form-item terms-checkbox-item"
          >
            <Checkbox>
              我已阅读并同意
              <button 
                type="button" 
                className="terms-link-inline"
                onClick={(e) => {
                  e.preventDefault();
                  setTermsModalVisible(true);
                }}
              >
                用户协议
              </button>
              和
              <button 
                type="button" 
                className="terms-link-inline"
                onClick={(e) => {
                  e.preventDefault();
                  setPrivacyModalVisible(true);
                }}
              >
                隐私政策
              </button>
            </Checkbox>
          </Form.Item>

          <Form.Item className="form-item">
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
              className="auth-button"
              size="large"
            >
              {isLoading ? '登录中...' : '登录'}
            </Button>
          </Form.Item>
        </Form>

        {/* Register Link */}
        <div className="auth-links">
          <span className="link-text">还没有账户？</span>
          <button 
            type="button" 
            className="link-button"
            onClick={onSwitchToRegister}
          >
            立即注册
          </button>
        </div>

      </div>
      
      <TermsModal
        visible={termsModalVisible}
        type="terms"
        onClose={() => setTermsModalVisible(false)}
      />
      <TermsModal
        visible={privacyModalVisible}
        type="privacy"
        onClose={() => setPrivacyModalVisible(false)}
      />
    </div>
  );
}; 